import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import "./notification.css";

export type NotificationKind = "success" | "error" | "warning" | "info";

export interface NotificationInput {
  message: string;
  kind?: NotificationKind;
  /** 0 keeps the notification open until the user closes it. */
  duration?: number;
  /** Reusing an id updates an existing notification instead of stacking it. */
  id?: string;
}

interface NotificationItem {
  id: string;
  message: string;
  kind: NotificationKind;
  expiresAt: number | null;
}

interface NotificationContextValue {
  notify: (input: NotificationInput) => string;
  update: (id: string, input: NotificationInput) => void;
  dismiss: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

let nextNotificationId = 0;

function createNotificationId(): string {
  nextNotificationId += 1;
  return `notification-${Date.now()}-${nextNotificationId}`;
}

function expiryFor(duration: number | undefined): number | null {
  if (duration === 0) return null;
  return Date.now() + (duration ?? 5000);
}

function notificationLabel(kind: NotificationKind): string {
  if (kind === "success") return "Erfolg";
  if (kind === "error") return "Fehler";
  if (kind === "warning") return "Hinweis";
  return "Information";
}

function NotificationIcon({ kind }: { kind: NotificationKind }) {
  const Icon =
    kind === "success"
      ? CheckCircle2
      : kind === "error"
        ? AlertCircle
        : kind === "warning"
          ? AlertTriangle
          : Info;
  return <Icon aria-hidden="true" />;
}

function NotificationToast({
  notification,
  onDismiss,
}: {
  notification: NotificationItem;
  onDismiss: (id: string) => void;
}) {
  const isError = notification.kind === "error";
  return (
    <div
      className={`notification notification-${notification.kind}`}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      aria-atomic="true"
    >
      <NotificationIcon kind={notification.kind} />
      <div className="notification-copy">
        <strong>{notificationLabel(notification.kind)}</strong>
        <span>{notification.message}</span>
      </div>
      <button
        type="button"
        className="notification-dismiss"
        aria-label="Benachrichtigung schließen"
        title="Benachrichtigung schließen"
        onClick={() => onDismiss(notification.id)}
      >
        <X aria-hidden="true" />
      </button>
    </div>
  );
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setNotifications((current) =>
      current.filter((notification) => notification.id !== id),
    );
  }, []);

  const notify = useCallback((input: NotificationInput): string => {
    const id = input.id ?? createNotificationId();
    const next: NotificationItem = {
      id,
      message: input.message,
      kind: input.kind ?? "info",
      expiresAt: expiryFor(input.duration),
    };
    setNotifications((current) => {
      const existing = current.some((notification) => notification.id === id);
      if (!existing) return [...current, next];
      return current.map((notification) =>
        notification.id === id ? next : notification,
      );
    });
    return id;
  }, []);

  const update = useCallback((id: string, input: NotificationInput) => {
    setNotifications((current) =>
      current.map((notification) =>
        notification.id === id
          ? {
              ...notification,
              message: input.message,
              kind: input.kind ?? notification.kind,
              expiresAt: expiryFor(input.duration),
            }
          : notification,
      ),
    );
  }, []);

  useEffect(() => {
    const deadlines = notifications
      .map((notification) => notification.expiresAt)
      .filter((expiresAt): expiresAt is number => expiresAt !== null);
    if (!deadlines.length) return;
    const timeout = window.setTimeout(
      () => {
        const now = Date.now();
        setNotifications((current) =>
          current.filter(
            (notification) =>
              notification.expiresAt === null || notification.expiresAt > now,
          ),
        );
      },
      Math.max(0, Math.min(...deadlines) - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [notifications]);

  const contextValue = useMemo(
    () => ({ notify, update, dismiss }),
    [dismiss, notify, update],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <div className="notification-viewport" aria-label="Benachrichtigungen">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      "useNotification muss innerhalb von NotificationProvider verwendet werden.",
    );
  return context;
}

/** Für eigenständige Datenhooks, die auch ohne App-Shell testbar bleiben. */
export function useOptionalNotification(): NotificationContextValue | null {
  return useContext(NotificationContext);
}
